import { useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { setResult, confirmResult, voidResult, setPairingStarted } from '@/lib/api';
import type { Round } from '@swiss-magic/shared-types';

type Input = {
  tournamentId: string | undefined;
  rounds: Round[];
  setRounds: Dispatch<SetStateAction<Round[]>>;
  handleActionError: (err: unknown, msg: string) => void;
};

export function useRoundResults({ tournamentId, rounds, setRounds, handleActionError }: Input) {
  const { t } = useTranslation('director');
  const [pendingAction, setPendingAction] = useState<{
    pairingId: string;
    type: 'result' | 'confirm';
  } | null>(null);

  async function handleSetResult(roundId: string, pairingId: string, result: string) {
    if (!tournamentId) return;
    // Capture original values for targeted rollback — avoids clobbering
    // unrelated concurrent optimistic updates by restoring only this pairing.
    const originalPairing = rounds
      .find((r) => r.id === roundId)
      ?.pairings.find((p) => p.id === pairingId);
    if (!originalPairing) return;
    const originalResult = originalPairing.result;
    const originalStatus = originalPairing.resultStatus;
    // Optimistic update
    setRounds((prev) =>
      prev.map((r) =>
        r.id === roundId
          ? { ...r, pairings: r.pairings.map((p) =>
              p.id === pairingId ? { ...p, result, resultStatus: 'pending' as const } : p,
            )}
          : r,
      ),
    );
    setPendingAction({ pairingId, type: 'result' });
    try {
      const updated = await setResult(tournamentId, roundId, pairingId, result);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err: unknown) {
      setRounds((prev) =>
        prev.map((r) =>
          r.id === roundId
            ? { ...r, pairings: r.pairings.map((p) =>
                p.id === pairingId
                  ? { ...p, result: originalResult, resultStatus: originalStatus }
                  : p,
              )}
            : r,
        ),
      );
      handleActionError(err, t('rounds_page.toast.set_result_failed'));
    } finally {
      setPendingAction(null);
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
  }

  async function handleConfirmResult(roundId: string, pairingId: string) {
    if (!tournamentId) return;
    // Capture original status for targeted rollback (see handleSetResult).
    const originalPairing = rounds
      .find((r) => r.id === roundId)
      ?.pairings.find((p) => p.id === pairingId);
    if (!originalPairing) return;
    const originalStatus = originalPairing.resultStatus;
    // Optimistic update
    setRounds((prev) =>
      prev.map((r) =>
        r.id === roundId
          ? { ...r, pairings: r.pairings.map((p) =>
              p.id === pairingId ? { ...p, resultStatus: 'confirmed' as const } : p,
            )}
          : r,
      ),
    );
    setPendingAction({ pairingId, type: 'confirm' });
    try {
      const updated = await confirmResult(tournamentId, roundId, pairingId);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err: unknown) {
      setRounds((prev) =>
        prev.map((r) =>
          r.id === roundId
            ? { ...r, pairings: r.pairings.map((p) =>
                p.id === pairingId ? { ...p, resultStatus: originalStatus } : p,
              )}
            : r,
        ),
      );
      handleActionError(err, t('rounds_page.toast.confirm_result_failed'));
    } finally {
      setPendingAction(null);
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
  }

  async function handleVoidResult(roundId: string, pairingId: string) {
    if (!tournamentId) return;
    setPendingAction({ pairingId, type: 'result' });
    try {
      const updated = await voidResult(tournamentId, roundId, pairingId);
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.void_result_failed'));
    } finally {
      setPendingAction(null);
    }
  }

  // Story 081 — flip the optional In Progress flag. No optimistic
  // update; the action is rare and the server response carries the
  // refreshed Round with the new started_at value.
  async function handleToggleInProgress(
    roundId: string,
    pairingId: string,
    started: boolean,
  ) {
    if (!tournamentId) return;
    try {
      const updated = await setPairingStarted(
        tournamentId,
        roundId,
        pairingId,
        started,
      );
      setRounds((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r)),
      );
    } catch (err) {
      handleActionError(err, t('rounds_page.toast.in_progress_failed'));
    }
  }

  return {
    pendingAction,
    handleSetResult,
    handleConfirmResult,
    handleVoidResult,
    handleToggleInProgress,
  };
}
